// src/pages/admin/AdminProfile.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  UserCircle,
  Edit,
  Save,
  Camera,
  X,
  Mail,
  Phone,
  Shield,
  Calendar,
  Lock,
  Key,
  Check,
  AlertCircle,
  ChevronDown,
  Upload,
  Globe,
  AlertTriangle
} from 'lucide-react';
import { 
  selectCurrentUser,
  selectAuthLoading,
  selectAdminUpdateSuccess,
  selectAdminError,
  selectAdminLoading
} from '../../store/slices/adminSlice';
import { 
  adminUpdateBasicInfo,
  adminUpdateEmail,
  adminUpdatePassword,
  adminUpdateFullProfile,
  adminUpdateTwoFactorAuth,
  adminUploadProfileImage,
  adminRemoveProfileImage,
  clearErrors,
  resetUpdateState
} from '../../store/slices/adminSlice';
import useTranslation from '../../hooks/useTranslation';
import colors from '../../constants/colors';
import styles from '../../styles/AdminProfile.module.css';

const AdminProfile = () => {
  const dispatch = useDispatch();
  const adminUser = useSelector(selectCurrentUser);
  const isLoading = useSelector(selectAuthLoading);
  const adminLoading = useSelector(selectAdminLoading);
  const updateSuccess = useSelector(selectAdminUpdateSuccess);
  const error = useSelector(selectAdminError);
  
  // Translations
  const { translatedText: profileTitle } = useTranslation('Profile');
  const { translatedText: editProfile } = useTranslation('Edit Profile');
  const { translatedText: saveChanges } = useTranslation('Save Changes');
  const { translatedText: cancel } = useTranslation('Cancel');
  const { translatedText: fullName } = useTranslation('Full Name');
  const { translatedText: email } = useTranslation('Email');
  const { translatedText: phone } = useTranslation('Phone');
  const { translatedText: role } = useTranslation('Role');
  const { translatedText: memberSince } = useTranslation('Member Since');
  const { translatedText: changePassword } = useTranslation('Change Password');
  const { translatedText: currentPassword } = useTranslation('Current Password');
  const { translatedText: newPassword } = useTranslation('New Password');
  const { translatedText: confirmPassword } = useTranslation('Confirm Password');
  const { translatedText: updatePassword } = useTranslation('Update Password');
  const { translatedText: profileUpdated } = useTranslation('Profile updated successfully');
  const { translatedText: passwordUpdated } = useTranslation('Password updated successfully');
  const { translatedText: emailUpdated } = useTranslation('Email updated successfully');
  const { translatedText: language } = useTranslation('Language');
  const { translatedText: selectLanguage } = useTranslation('Select Language');
  const { translatedText: twoFactorAuth } = useTranslation('Two-Factor Authentication');
  const { translatedText: enable2FA } = useTranslation('Enable 2FA');
  const { translatedText: disable2FA } = useTranslation('Disable 2FA');

  // State management
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    language: 'en',
    profileImage: null
  });
  const [emailData, setEmailData] = useState({
    newEmail: '',
    currentPassword: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Supported languages
  const languages = [
    { code: 'en', name: 'English' },
    { code: 'ht', name: 'Kreyòl' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' }
  ];

  // Load user data
  useEffect(() => {
    if (adminUser) {
      setFormData({
        name: adminUser.name || '',
        email: adminUser.email || '',
        phone: adminUser.phone || '',
        language: adminUser.language || 'en',
        profileImage: adminUser.profileImage?.url || null
      });
      if (adminUser.profileImage?.url) {
        setPreviewImage(adminUser.profileImage.url);
      }
      setTwoFactorEnabled(adminUser.twoFactorAuth?.enabled || false);
    }
  }, [adminUser]);

  // Handle success/error messages
  useEffect(() => {
    if (updateSuccess) {
      setSuccessMessage(profileUpdated || 'Profile updated successfully');
      setTimeout(() => {
        setSuccessMessage('');
        // Reset update success state after message disappears
        dispatch(resetUpdateState());
      }, 5000);
    }
  }, [updateSuccess, profileUpdated, dispatch]);

  useEffect(() => {
    if (error) {
      setErrorMessage(error);
      setTimeout(() => {
        setErrorMessage('');
        // Clear error after message disappears
        dispatch(clearErrors());
      }, 5000);
    }
  }, [error, dispatch]);

  // Clear messages when starting new actions
  const clearMessages = () => {
    setSuccessMessage('');
    setErrorMessage('');
    dispatch(clearErrors());
    dispatch(resetUpdateState());
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    clearMessages();
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    clearMessages();
    setEmailData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    clearMessages();
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    clearMessages();

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage('Image size must be less than 5MB');
      e.target.value = '';
      return;
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setErrorMessage('Please upload a valid image (JPEG, PNG, GIF, or WEBP)');
      e.target.value = '';
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload to server
    await uploadProfileImage(file);
    e.target.value = '';
  };

  const uploadProfileImage = async (file) => {
    clearMessages();
    setUploadingImage(true);

    try {
      const adminId = adminUser._id || adminUser.id;
      const result = await dispatch(adminUploadProfileImage({ id: adminId, file })).unwrap();
      
      if (result.success) {
        setSuccessMessage('Profile image uploaded successfully');
        // Update formData with new image URL
        if (result.data?.profileImage?.url) {
          setFormData(prev => ({
            ...prev,
            profileImage: result.data.profileImage.url
          }));
          setPreviewImage(result.data.profileImage.url);
        }
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      }
    } catch (error) {
      setErrorMessage(error || 'Failed to upload profile image');
      // Revert preview if upload fails
      setPreviewImage(adminUser?.profileImage?.url || null);
      setTimeout(() => {
        setErrorMessage('');
      }, 5000);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = async () => {
    if (!window.confirm('Are you sure you want to remove your profile image?')) {
      return;
    }

    clearMessages();
    setUploadingImage(true);

    try {
      const adminId = adminUser._id || adminUser.id;
      const result = await dispatch(adminRemoveProfileImage(adminId)).unwrap();
      
      if (result.success) {
        setSuccessMessage('Profile image removed successfully');
        setPreviewImage(null);
        setFormData(prev => ({
          ...prev,
          profileImage: null
        }));
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      }
    } catch (error) {
      setErrorMessage(error || 'Failed to remove profile image');
      setTimeout(() => {
        setErrorMessage('');
      }, 5000);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessages();

    try {
      // Prepare update data
      const updateData = {
        name: formData.name,
        phone: formData.phone,
        language: formData.language
      };

      const result = await dispatch(adminUpdateBasicInfo({
        id: adminUser._id || adminUser.id,
        updateData
      })).unwrap();

      if (result.success) {
        setSuccessMessage(profileUpdated || 'Profile updated successfully');
        setIsEditing(false);
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      }
    } catch (error) {
      setErrorMessage(error || 'Failed to update profile');
      setTimeout(() => {
        setErrorMessage('');
      }, 5000);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    clearMessages();

    // Validate email format
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(emailData.newEmail)) {
      setErrorMessage('Please enter a valid email address');
      setTimeout(() => {
        setErrorMessage('');
      }, 5000);
      return;
    }

    if (!emailData.currentPassword) {
      setErrorMessage('Current password is required to change email');
      setTimeout(() => {
        setErrorMessage('');
      }, 5000);
      return;
    }

    try {
      const result = await dispatch(adminUpdateEmail({
        id: adminUser._id || adminUser.id,
        newEmail: emailData.newEmail,
        currentPassword: emailData.currentPassword
      })).unwrap();

      if (result.success) {
        setSuccessMessage(emailUpdated || 'Email updated successfully');
        setIsEditingEmail(false);
        setEmailData({
          newEmail: '',
          currentPassword: ''
        });
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      }
    } catch (error) {
      setErrorMessage(error || 'Failed to update email');
      setTimeout(() => {
        setErrorMessage('');
      }, 5000);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    clearMessages();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setErrorMessage('Passwords do not match');
      setTimeout(() => {
        setErrorMessage('');
      }, 5000);
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      setTimeout(() => {
        setErrorMessage('');
      }, 5000);
      return;
    }

    if (!passwordData.currentPassword) {
      setErrorMessage('Current password is required');
      setTimeout(() => {
        setErrorMessage('');
      }, 5000);
      return;
    }

    try {
      const result = await dispatch(adminUpdatePassword({
        id: adminUser._id || adminUser.id,
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      })).unwrap();

      if (result.success) {
        setSuccessMessage(passwordUpdated || 'Password updated successfully');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswordChange(false);
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      }
    } catch (error) {
      setErrorMessage(error || 'Failed to update password');
      setTimeout(() => {
        setErrorMessage('');
      }, 5000);
    }
  };

  const handleTwoFactorToggle = async () => {
    clearMessages();

    try {
      const result = await dispatch(adminUpdateTwoFactorAuth({
        id: adminUser._id || adminUser.id,
        enabled: !twoFactorEnabled
      })).unwrap();

      if (result.success) {
        setTwoFactorEnabled(!twoFactorEnabled);
        setSuccessMessage(
          twoFactorEnabled 
            ? 'Two-factor authentication disabled' 
            : 'Two-factor authentication enabled'
        );
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      }
    } catch (error) {
      setErrorMessage(error || 'Failed to update two-factor authentication');
      setTimeout(() => {
        setErrorMessage('');
      }, 5000);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className={styles.profileContainer}>
      <div className={styles.profileHeader}>
        <div className={styles.profileTitleSection}>
          <UserCircle size={32} color={colors.primary} />
          <h2>{profileTitle || 'Profile'}</h2>
        </div>
        <div className={styles.profileActions}>
          {!isEditing ? (
            <button 
              className={styles.editButton}
              onClick={() => {
                clearMessages();
                setIsEditing(true);
              }}
            >
              <Edit size={18} />
              {editProfile || 'Edit Profile'}
            </button>
          ) : (
            <button 
              className={styles.cancelButton}
              onClick={() => {
                clearMessages();
                setIsEditing(false);
                setFormData({
                  name: adminUser?.name || '',
                  email: adminUser?.email || '',
                  phone: adminUser?.phone || '',
                  language: adminUser?.language || 'en',
                  profileImage: adminUser?.profileImage?.url || null
                });
                setPreviewImage(adminUser?.profileImage?.url || null);
              }}
            >
              <X size={18} />
              {cancel || 'Cancel'}
            </button>
          )}
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className={styles.successMessage}>
          <Check size={20} />
          <span>{successMessage}</span>
        </div>
      )}
      {errorMessage && (
        <div className={styles.errorMessage}>
          <AlertCircle size={20} />
          <span>{errorMessage}</span>
        </div>
      )}

      <div className={styles.profileContent}>
        {/* Profile Image */}
        <div className={styles.profileImageSection}>
          <div className={styles.profileImageWrapper}>
            {uploadingImage ? (
              <div className={styles.profileImageLoading}>
                <div className={styles.spinner}></div>
              </div>
            ) : previewImage ? (
              <img 
                src={previewImage} 
                alt="Profile" 
                className={styles.profileImage}
              />
            ) : (
              <div className={styles.profileImagePlaceholder}>
                <UserCircle size={64} color={colors.primary} />
              </div>
            )}
            <div className={styles.imageActions}>
              <label className={styles.imageUploadLabel}>
                <Camera size={20} />
                <input 
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className={styles.imageUploadInput}
                  disabled={uploadingImage}
                />
              </label>
              {previewImage && (
                <button 
                  className={styles.imageRemoveButton}
                  onClick={handleRemoveImage}
                  disabled={uploadingImage}
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>
          <p className={styles.imageUploadHint}>
            Click camera icon to upload new image (Max 5MB)
          </p>
        </div>

        {/* Profile Info */}
        <form onSubmit={handleSubmit} className={styles.profileForm}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="name">
                {fullName || 'Full Name'}
              </label>
              {isEditing ? (
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={styles.formInput}
                  required
                  placeholder="Enter your full name"
                />
              ) : (
                <div className={styles.formValue}>
                  <UserCircle size={16} />
                  <span>{formData.name || 'N/A'}</span>
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label>{email || 'Email'}</label>
              {!isEditingEmail ? (
                <div className={styles.formValue}>
                  <Mail size={16} />
                  <span>{formData.email || 'N/A'}</span>
                  <button
                    type="button"
                    className={styles.changeEmailButton}
                    onClick={() => {
                      clearMessages();
                      setIsEditingEmail(true);
                    }}
                  >
                    <Edit size={14} />
                    Change
                  </button>
                </div>
              ) : (
                <div className={styles.emailEditContainer}>
                  <input
                    type="email"
                    name="newEmail"
                    value={emailData.newEmail}
                    onChange={handleEmailChange}
                    className={styles.formInput}
                    placeholder="New email address"
                    required
                  />
                  <input
                    type="password"
                    name="currentPassword"
                    value={emailData.currentPassword}
                    onChange={handleEmailChange}
                    className={styles.formInput}
                    placeholder="Current password"
                    required
                  />
                  <div className={styles.emailActions}>
                    <button
                      type="button"
                      className={styles.saveEmailButton}
                      onClick={handleEmailSubmit}
                      disabled={isLoading}
                    >
                      <Save size={16} />
                      {isLoading ? 'Saving...' : 'Save Email'}
                    </button>
                    <button
                      type="button"
                      className={styles.cancelEmailButton}
                      onClick={() => {
                        clearMessages();
                        setIsEditingEmail(false);
                        setEmailData({
                          newEmail: '',
                          currentPassword: ''
                        });
                      }}
                    >
                      <X size={16} />
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="phone">
                {phone || 'Phone'}
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={styles.formInput}
                  placeholder="Enter phone number"
                />
              ) : (
                <div className={styles.formValue}>
                  <Phone size={16} />
                  <span>{formData.phone || 'N/A'}</span>
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="language">
                {language || 'Language'}
              </label>
              {isEditing ? (
                <select
                  id="language"
                  name="language"
                  value={formData.language}
                  onChange={handleInputChange}
                  className={styles.formSelect}
                >
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              ) : (
                <div className={styles.formValue}>
                  <Globe size={16} />
                  <span>
                    {languages.find(l => l.code === formData.language)?.name || 'English'}
                  </span>
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label>{role || 'Role'}</label>
              <div className={styles.formValue}>
                <Shield size={16} />
                <span className={styles.roleBadge}>
                  {adminUser?.role || 'Administrator'}
                </span>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>{memberSince || 'Member Since'}</label>
              <div className={styles.formValue}>
                <Calendar size={16} />
                <span>{formatDate(adminUser?.createdAt)}</span>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>Status</label>
              <div className={styles.formValue}>
                <div className={`${styles.statusBadge} ${
                  adminUser?.accountStatus === 'active' ? styles.statusActive :
                  adminUser?.accountStatus === 'suspended' ? styles.statusSuspended :
                  styles.statusInactive
                }`}>
                  <span className={styles.statusDot}></span>
                  <span>{adminUser?.accountStatus || 'Active'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Save Changes Button */}
          {isEditing && (
            <div className={styles.formActions}>
              <button 
                type="submit" 
                className={styles.saveButton}
                disabled={isLoading}
              >
                <Save size={18} />
                {isLoading ? 'Saving...' : (saveChanges || 'Save Changes')}
              </button>
            </div>
          )}
        </form>

        {/* Password Change Section */}
        <div className={styles.passwordSection}>
          <button 
            className={styles.passwordToggle}
            onClick={() => {
              clearMessages();
              setShowPasswordChange(!showPasswordChange);
            }}
          >
            <Lock size={18} />
            <span>{changePassword || 'Change Password'}</span>
            <ChevronDown 
              size={18} 
              className={`${styles.chevron} ${showPasswordChange ? styles.chevronOpen : ''}`}
            />
          </button>

          {showPasswordChange && (
            <form onSubmit={handlePasswordSubmit} className={styles.passwordForm}>
              <div className={styles.formGroup}>
                <label htmlFor="currentPassword">
                  {currentPassword || 'Current Password'}
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  className={styles.formInput}
                  required
                  placeholder="Enter current password"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="newPassword">
                  {newPassword || 'New Password'}
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  className={styles.formInput}
                  required
                  minLength="8"
                  placeholder="Enter new password (min 8 characters)"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="confirmPassword">
                  {confirmPassword || 'Confirm Password'}
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  className={styles.formInput}
                  required
                  placeholder="Confirm new password"
                />
              </div>

              <button 
                type="submit" 
                className={styles.updatePasswordButton}
                disabled={isLoading}
              >
                <Key size={18} />
                {isLoading ? 'Updating...' : (updatePassword || 'Update Password')}
              </button>
            </form>
          )}
        </div>

        {/* Two-Factor Authentication Section */}
        <div className={styles.twoFactorSection}>
          <div className={styles.twoFactorHeader}>
            <Shield size={20} />
            <span>{twoFactorAuth || 'Two-Factor Authentication'}</span>
          </div>
          <div className={styles.twoFactorContent}>
            <div className={styles.twoFactorStatus}>
              <span className={twoFactorEnabled ? styles.statusEnabled : styles.statusDisabled}>
                {twoFactorEnabled ? '✅ Enabled' : '❌ Disabled'}
              </span>
            </div>
            <button
              className={`${styles.twoFactorButton} ${
                twoFactorEnabled ? styles.disableButton : styles.enableButton
              }`}
              onClick={handleTwoFactorToggle}
              disabled={isLoading}
            >
              {twoFactorEnabled ? (disable2FA || 'Disable 2FA') : (enable2FA || 'Enable 2FA')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;